import {
  Outlet,
  RouterContextProvider,
  createMemoryHistory,
  createRootRoute,
  createRouter,
  useBlocker as useTanStackBlocker,
  useLocation as useTanStackLocation,
  useMatches,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import type { AnyRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { matchRoutes } from './match-route';

type SearchParamsInit =
  | string
  | URLSearchParams
  | Record<string, string>
  | Array<[string, string]>
  | ((previous: URLSearchParams) => URLSearchParams);

type SetSearchParamsOptions = {
  replace?: boolean;
  state?: Record<string, unknown>;
};

type LegacyLocationState = Record<string, unknown> | null;

type LegacyBlocker = {
  state: 'blocked' | 'unblocked';
  proceed?: () => void;
  reset?: () => void;
};

type MemoryRouterProps = {
  children?: React.ReactNode;
  initialEntries?: Array<string>;
  initialIndex?: number;
};

export { Outlet, useMatches, useNavigate };

export function useLocation() {
  const location = useTanStackLocation();
  return useMemo(
    () => ({
      ...location,
      search: location.searchStr,
      state: location.state as unknown as LegacyLocationState,
    }),
    [location],
  );
}

type LegacyParamsOptions = {
  strict?: boolean;
};

export function useParams<TParams extends Record<string, string | undefined> = Record<string, string | undefined>>(
  _options?: LegacyParamsOptions,
) {
  const matches = useMatches();
  return useMemo(() => {
    const params = mergeMatchParams(matches);
    return params as TParams;
  }, [matches]);
}

export function mergeMatchParams(matches: Array<{ params: Record<string, unknown> }>) {
  return matches.reduce<Record<string, string | undefined>>(
    (accumulator, match) => ({ ...accumulator, ...stringifyMatchParams(match.params) }),
    {},
  );
}

function stringifyMatchParams(params: Record<string, unknown>) {
  // TanStack route param parsers may return numbers; legacy useParams callers
  // still expect React Router-style strings.
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value == null ? undefined : String(value)]),
  );
}

export function useBlocker(isBlocking: boolean): LegacyBlocker {
  const blocker = useTanStackBlocker({
    shouldBlockFn: () => isBlocking,
    disabled: !isBlocking,
    withResolver: true,
  });

  return {
    state: blocker.status === 'blocked' ? 'blocked' : 'unblocked',
    proceed: blocker.proceed,
    reset: blocker.reset,
  };
}

export function useSearchParams(): [
  URLSearchParams,
  (nextInit: SearchParamsInit, options?: SetSearchParamsOptions) => void,
] {
  const location = useTanStackLocation();
  const router = useRouter();

  const searchParams = useMemo(() => new URLSearchParams(location.searchStr), [location.searchStr]);

  const setSearchParams = useCallback(
    (nextInit: SearchParamsInit, options?: SetSearchParamsOptions) => {
      const previousSearchParams = new URLSearchParams(location.searchStr);
      const nextParams = typeof nextInit === 'function' ? nextInit(previousSearchParams) : nextInit;
      const nextSearchParams = new URLSearchParams(nextParams);
      const search = nextSearchParams.toString();
      const hash = formatHash(location.hash);
      void router.navigate({
        to: asLegacyRouterPath(`${location.pathname}${search.length > 0 ? `?${search}` : ''}${hash}`),
        replace: options?.replace,
        state: options?.state,
      });
    },
    [location.hash, location.pathname, location.searchStr, router],
  );

  return [searchParams, setSearchParams];
}

function formatHash(hash: string) {
  if (hash.length === 0) return '';
  return hash.startsWith('#') ? hash : `#${hash}`;
}

export function useBeforeUnload(handler: (event: BeforeUnloadEvent) => void, options?: AddEventListenerOptions) {
  useEffect(() => {
    window.addEventListener('beforeunload', handler, options);
    return () => {
      window.removeEventListener('beforeunload', handler, options);
    };
  }, [handler, options]);
}

export function useMatch(pattern: string) {
  const location = useTanStackLocation();
  return matchRoutes(location.pathname, [asLegacyRoutePattern(pattern)]) ? { pathname: location.pathname } : null;
}

export function MemoryRouter({ children, initialEntries = ['/'], initialIndex }: MemoryRouterProps) {
  const initialEntriesKey = initialEntries.join('\u0000');
  const stableInitialEntries = useMemo(() => initialEntries, [initialEntriesKey]);

  const router = useMemo(() => {
    if (typeof window !== 'undefined') {
      // MemoryRouter is test-only compat; TanStack memory history still touches
      // scroll restoration APIs that jsdom/browser-test shims may not provide.
      Object.defineProperty(window, 'scrollTo', { value: () => {}, writable: true });
    }

    const routeTree = createRootRoute();

    return createRouter({
      routeTree,
      history: createMemoryHistory({ initialEntries: stableInitialEntries, initialIndex }),
      scrollRestoration: false,
    });
  }, [stableInitialEntries, initialIndex]);

  return <RouterContextProvider router={router}>{children}</RouterContextProvider>;
}

function asLegacyRouterPath(path: string) {
  return path as never;
}

function asLegacyRoutePattern(pattern: string) {
  return pattern as unknown as AnyRoute['path'];
}
